import { create } from 'zustand';
import { runbooksApi, Runbook } from '@/lib/runbooks';
import { useAccountStore, useSubscriptionStore } from '@nimbitlabs/features';

interface RunbooksState {
  runbooks: Runbook[];
  loading: boolean;
  error: string | null;
  
  fetchRunbooks: () => Promise<void>;
  getRunbook: (id: string) => Promise<Runbook>;
  createRunbook: (data: any) => Promise<Runbook>;
  updateRunbook: (id: string, data: any) => Promise<Runbook>;
  deleteRunbook: (id: string) => Promise<void>;
  executeRunbook: (id: string) => Promise<void>;
  hasReachedLimit: () => boolean;
  getRunbookLimit: () => { current: number; limit: number | null; hasReachedLimit: boolean };
  clearError: () => void;
}

const runbooksStore = create<RunbooksState>((set, get) => ({
  runbooks: [],
  loading: false,
  error: null,
  
  fetchRunbooks: async () => {
    set({ loading: true, error: null });
    try {
      const runbooks = await runbooksApi.getRunbooks();
      set({ runbooks, loading: false });
    } catch (error: any) {
      set({ 
        error: error?.message ?? 'Failed to fetch runbooks', 
        loading: false 
      });
      throw error;
    }
  },
  
  getRunbook: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const runbook = await runbooksApi.getRunbook(id);
      set({ loading: false });
      return runbook;
    } catch (error: any) {
      set({ error: error?.message ?? 'Failed to fetch runbook', loading: false });
      throw error;
    }
  },
  
  createRunbook: async (data: any) => {
    set({ error: null });
    try {
      const newRunbook = await runbooksApi.createRunbook(data);
      set(state => ({ 
        runbooks: [newRunbook, ...state.runbooks] 
      }));
      return newRunbook;
    } catch (error: any) {
      set({ error: error?.message ?? 'Failed to create runbook' });
      throw error;
    }
  },
  
  updateRunbook: async (id: string, data: any) => {
    set({ error: null });
    try {
      const updatedRunbook = await runbooksApi.updateRunbook(id, data);
      set(state => ({
        runbooks: state.runbooks.map(r => r.id === id ? updatedRunbook : r)
      }));
      return updatedRunbook;
    } catch (error: any) {
      set({ error: error?.message ?? 'Failed to update runbook' });
      throw error;
    }
  },
  
  deleteRunbook: async (id: string) => {
    set({ error: null });
    try {
      await runbooksApi.deleteRunbook(id);
      set(state => ({
        runbooks: state.runbooks.filter(r => r.id !== id)
      }));
    } catch (error: any) {
      set({ error: error?.message ?? 'Failed to delete runbook' });
      throw error;
    }
  },
  
  executeRunbook: async (id: string) => {
    set({ error: null });
    try {
      await runbooksApi.executeRunbook(id);
      // Optionally update runbook execution count or last executed time
      await get().fetchRunbooks();
    } catch (error: any) {
      set({ error: error?.message ?? 'Failed to execute runbook' });
      throw error;
    }
  },
  
  hasReachedLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    if (!limits) return true; // No data = assume limit reached
    const runbookLimit = limits.features?.runbooks?.limit;
    if (runbookLimit === undefined) return false; // No limit = never reached
    // Use actual count from store, not backend usage
    const currentCount = get().runbooks.length;
    return currentCount >= runbookLimit;
  },
  
  getRunbookLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    // Use actual count from store, not backend usage
    const current = get().runbooks.length;
    const limit = limits?.features?.runbooks?.limit ?? null;
    const hasReachedLimit = limit !== null && current >= limit;
    return { current, limit, hasReachedLimit };
  },
  
  clearError: () => set({ error: null }),
}));

export const useRunbooksStore = runbooksStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      runbooksStore.getState().fetchRunbooks();
    } else {
      runbooksStore.setState({ runbooks: [] });
    }
  }
);
